import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, Zap } from 'lucide-react';

const countryCodes = [
  { code: '+1', country: 'US/Canada', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '+44', country: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '+91', country: 'India', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '+86', country: 'China', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: '+81', country: 'Japan', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: '+49', country: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: '+33', country: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: '+61', country: 'Australia', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '+55', country: 'Brazil', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: '+971', country: 'UAE', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: '+966', country: 'Saudi Arabia', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: '+92', country: 'Pakistan', flag: '\u{1F1F5}\u{1F1F0}' },
  { code: '+880', country: 'Bangladesh', flag: '\u{1F1E7}\u{1F1E9}' },
  { code: '+234', country: 'Nigeria', flag: '\u{1F1F3}\u{1F1EC}' },
  { code: '+82', country: 'South Korea', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: '+39', country: 'Italy', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: '+34', country: 'Spain', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: '+7', country: 'Russia', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: '+52', country: 'Mexico', flag: '\u{1F1F2}\u{1F1FD}' },
  { code: '+62', country: 'Indonesia', flag: '\u{1F1EE}\u{1F1E9}' },
  { code: '+90', country: 'Turkey', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: '+27', country: 'South Africa', flag: '\u{1F1FF}\u{1F1E6}' },
  { code: '+63', country: 'Philippines', flag: '\u{1F1F5}\u{1F1ED}' },
  { code: '+84', country: 'Vietnam', flag: '\u{1F1FB}\u{1F1F3}' },
  { code: '+66', country: 'Thailand', flag: '\u{1F1F9}\u{1F1ED}' },
  { code: '+60', country: 'Malaysia', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: '+20', country: 'Egypt', flag: '\u{1F1EA}\u{1F1EC}' },
  { code: '+48', country: 'Poland', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: '+31', country: 'Netherlands', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: '+46', country: 'Sweden', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: '+41', country: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}' },
  { code: '+65', country: 'Singapore', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: '+64', country: 'New Zealand', flag: '\u{1F1F3}\u{1F1FF}' },
  { code: '+353', country: 'Ireland', flag: '\u{1F1EE}\u{1F1EA}' },
  { code: '+972', country: 'Israel', flag: '\u{1F1EE}\u{1F1F1}' },
  { code: '+254', country: 'Kenya', flag: '\u{1F1F0}\u{1F1EA}' },
  { code: '+233', country: 'Ghana', flag: '\u{1F1EC}\u{1F1ED}' },
  { code: '+57', country: 'Colombia', flag: '\u{1F1E8}\u{1F1F4}' },
  { code: '+56', country: 'Chile', flag: '\u{1F1E8}\u{1F1F1}' },
  { code: '+54', country: 'Argentina', flag: '\u{1F1E6}\u{1F1F7}' },
];

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handlePhoneChange = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length <= 10) {
      setFormData({ ...formData, phone: d });
      setErrors({ ...errors, phone: d && d.length !== 10 ? 'Must be 10 digits' : '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const ne = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { ne.email = 'Valid email required'; hasErrors = true; }
    if (formData.phone.length !== 10) { ne.phone = 'Must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(ne); return; }

    setIsSubmitting(true);
    setTimeout(() => {
      toast({ title: "Request Submitted", description: "Our trademark experts will contact you within 24 hours." });
      setIsSubmitting(false);
      setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
      setErrors({ email: '', phone: '' });
      onClose();
    }, 1500);
  };

  const inputCls = "h-10 bg-white/[0.05] border-white/[0.08] rounded-lg text-white placeholder:text-[#52525B] focus:border-[#3B82F6]/50 focus:ring-1 focus:ring-[#3B82F6]/30";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-[900px] p-0 gap-0 overflow-hidden max-h-[90vh] rounded-2xl border border-white/[0.08] bg-[#09090B]">
        <DialogTitle className="sr-only">Contact Form</DialogTitle>
        <div className="flex flex-col md:flex-row h-full max-h-[90vh]">
          {/* Left */}
          <div className="relative w-full md:w-[40%] min-h-[200px] md:min-h-[500px] overflow-hidden">
            <div className="absolute inset-0 btn-gradient opacity-80" />
            <div className="absolute inset-0 bg-[url('https://static.prod-images.emergentagent.com/jobs/73b8c8aa-3ecf-4f03-986d-12df14dd70ca/images/938de7fddf2994cfc84d0a60afd1ed9dc1ac12c37cc5e15fe50b15acf29f945a.png')] bg-cover bg-center mix-blend-overlay opacity-30" />
            <div className="relative p-8 flex flex-col justify-end h-full">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/80 uppercase tracking-wider mb-3">
                <Zap className="w-3.5 h-3.5" /> Expert Protection
              </div>
              <h3 className="font-heading text-3xl font-semibold text-white tracking-tight mb-2 leading-tight">
                Protect Your<br />Brand Identity.
              </h3>
              <p className="text-white/60 text-sm">Professional trademark attorneys ready to secure your brand nationwide.</p>
            </div>
          </div>

          {/* Right - Form */}
          <div className="w-full md:w-[60%] p-6 md:p-8 max-h-[90vh] overflow-y-auto">
            <div className="mb-5">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#06B6D4] uppercase tracking-wider mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#06B6D4]"></span> Start Registration
              </div>
              <h2 className="font-heading text-xl font-semibold text-white">Get Expert Trademark Protection</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <Label htmlFor="dialog-name" className="text-[#A1A1AA] font-medium text-xs mb-1.5 block">Name *</Label>
                <Input data-testid="dialog-name-input" id="dialog-name" placeholder="Your full name" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className={inputCls} />
              </div>
              <div>
                <Label htmlFor="dialog-email" className="text-[#A1A1AA] font-medium text-xs mb-1.5 block">Email *</Label>
                <Input data-testid="dialog-email-input" id="dialog-email" type="email" placeholder="you@company.com" value={formData.email}
                  onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setErrors({ ...errors, email: '' }); }} required
                  className={`${inputCls} ${errors.email ? 'border-red-500' : ''}`} />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="dialog-phone" className="text-[#A1A1AA] font-medium text-xs mb-1.5 block">Phone *</Label>
                <div className="flex gap-2">
                  <Select value={formData.countryCode} onValueChange={(v) => setFormData({ ...formData, countryCode: v })}>
                    <SelectTrigger data-testid="dialog-country-select" className="w-[100px] h-10 bg-white/[0.05] border-white/[0.08] rounded-lg text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181B] border-[#27272A] max-h-[250px]">
                      {countryCodes.map((item) => (
                        <SelectItem key={item.code} value={item.code}><span className="flex items-center gap-1.5"><span>{item.flag}</span><span>{item.code}</span></span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input data-testid="dialog-phone-input" id="dialog-phone" type="tel" placeholder="1234567890" value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10}
                    className={`flex-1 ${inputCls} ${errors.phone ? 'border-red-500' : ''}`} />
                </div>
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
              </div>
              <div>
                <Label htmlFor="dialog-desc" className="text-[#A1A1AA] font-medium text-xs mb-1.5 block">Description</Label>
                <Textarea data-testid="dialog-description-input" id="dialog-desc" placeholder="What are you looking to protect?" rows={2} value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-white/[0.05] border-white/[0.08] rounded-lg text-white placeholder:text-[#52525B] focus:border-[#3B82F6]/50 focus:ring-1 focus:ring-[#3B82F6]/30 resize-none" />
              </div>
              <Button data-testid="dialog-submit-btn" type="submit" disabled={isSubmitting}
                className="w-full btn-gradient text-white font-semibold h-11 rounded-lg border-0">
                {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : 'Submit Request'}
              </Button>
              <p className="text-xs text-[#52525B] text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
